/* eslint-disable max-lines */
import { LinearProgress, TablePagination } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import _orderBy from 'lodash/orderBy';
import React, { useEffect, useState } from 'react';
import { useArrayState } from 'react-use-object-state';
import { parseQuery } from '../..';
import { JupiterTheme } from '../../theme/types';
import {
  PaginationSortingProps,
  QueryResultsChange,
  QueryResultsChangeType,
} from '../../types';
import { isNotAnAtProperty } from '../../util/atProperty';
import { EntityDrawerHookProvider } from '../drawers/EntityDrawer/useEntityDrawerHook';
import QueryResultTableBody from './QueryResultTableBody';
import buildOrderByNode from '../../util/buildOrderByNode';
import flattenGraphObject from './util/flattenGraphObject';
import { J1QLListData } from '../../../types/J1QL';

const ROWS_PER_PAGE_OPTIONS = [50, 125, 250];
// _meta field contains metadata about the table row
const TABLE_ROW_PROPERTIES_TO_IGNORE_SET = new Set(['_meta']);

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    tableContainer: {
      overflow: 'scroll',
    },
    loadingContainer: {
      display: 'flex',
      alignItems: 'center',
      height: '100%',
    },
  })
);

const hasMoreData = (result: J1QLListData) => {
  return (
    result.data &&
    result.data?.length % Math.max(...ROWS_PER_PAGE_OPTIONS) === 0
  );
};

type Sort = 'asc' | 'desc';

interface Props {
  result: J1QLListData;
  query: string;
  paginationProps?: PaginationSortingProps;
  onResultsChange?: (changes: QueryResultsChange[]) => void;
  disableRowClick?: boolean;
  useRowDataForEntityDrawer?: boolean;
  noDataEdit?: boolean;
  parentSelectedRowID?: string | null;
  parentSetSelectedRowID?: (id: string | null) => void;
}

export const QueryResultTable = ({
  result,
  query,
  paginationProps,
  onResultsChange,
  disableRowClick,
  useRowDataForEntityDrawer,
  noDataEdit,
  parentSelectedRowID,
  parentSetSelectedRowID,
}: Props) => {
  const classes = useStyles();
  const { loadMoreOrderBy, loading, hasNextPage } = paginationProps || {};

  const [selectedRowID, setSelectedRowID] = useState<string | null | undefined>(
    null
  );
  const columns = useArrayState<any>([]);
  const sortedData = useArrayState<any>([]);
  const [sort, setSort] = useState<Sort>('asc');
  const [sortedColumn, setSortedColumn] = useState<string>('');

  // compute column and sorted data when result or query changes
  useEffect(() => {
    const handleParseListResult = () => {
      if (result.data) {
        const { data } = result;

        // collect all properties first
        let properties = {};
        let sortedDataValues: any[] = [];

        for (const item of data) {
          const flattened = flattenGraphObject(item);
          sortedDataValues.push(flattened);
          properties = { ...flattened, ...properties };
        }
        sortedData.setState(sortedDataValues);

        columns.setState([
          // make class, type, and displayName the first props in table
          '_class',
          '_type',
          'displayName',
          // filter out underscore props and @ props
          ...Object.keys(properties)
            .filter(
              (prop) =>
                !prop.startsWith('_') &&
                prop !== 'displayName' &&
                isNotAnAtProperty(prop)
            )
            .sort(),
        ]);
      }
    };

    const handleParseTableResult = () => {
      if (result.data) {
        const { data } = result;
        const ast = parseQuery(query);

        const columnMap: { [key: string]: boolean } = {};

        if (ast?.return?.projections.selectors) {
          const { selectors } = ast.return.projections;
          let expressionCount = 0;
          for (const selector of selectors) {
            if (selector.type === 'projectionSelector') {
              const { alias, sourceIdentifier, field } = selector;

              // skip * fields and aggregation functions
              if (selector.field !== '*' && !selector.aggregatingFunction) {
                const column = alias || `${sourceIdentifier}.${field}`;
                columnMap[column] = true;
              }
            } else if (selector.type === 'expressionNode') {
              const { alias } = selector;
              const column = alias || `expression${expressionCount++}`;
              columnMap[column] = true;
            }
          }
        }

        // because of the existence of * fields,
        // we need to still collect all the fields we can
        columns.setState(
          Object.keys(
            data.reduce((map: any, row: any) => {
              Object.keys(row).forEach((key) => {
                if (!TABLE_ROW_PROPERTIES_TO_IGNORE_SET.has(key)) {
                  map[key] = true;
                }
              });
              return map;
            }, columnMap)
          )
        );
        sortedData.setState(data);
      }
    };

    if (result.type === 'list') {
      handleParseListResult();
    } else {
      handleParseTableResult();
    }
  }, [result.data, query]);

  const onSortClick = (column: string) => {
    let newSort: Sort = 'asc';
    if (column === sortedColumn) {
      newSort = sort === 'asc' ? 'desc' : 'asc';
    }
    setSort(newSort);
    setSortedColumn(column);
    if (hasMoreData(result)) {
      setPage(0);
      loadMoreOrderBy?.({ orderBy: buildOrderByNode(query, column, newSort) });
    } else {
      sortedData.setState(
        _orderBy(
          sortedData.state,
          [(row: any) => String(row[column]).toLowerCase()],
          [newSort]
        )
      );
    }
  };

  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);

  const handleChangePage = async (_: any, pageIndex: number) => {
    setPage(pageIndex);
    if (sortedData.state.length <= pageIndex * rowsPerPage) {
      loadMoreOrderBy?.({
        orderBy: sortedColumn
          ? buildOrderByNode(query, sortedColumn, sort)
          : undefined,
      });
    }
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  const handleClick = (row: any) => {
    if (!disableRowClick) {
      if (parentSetSelectedRowID) parentSetSelectedRowID(row._vertexID || null);
      else setSelectedRowID(row._vertexID || null);
    }
  };

  const showPaginationFooter =
    paginationProps && !loading && rowsPerPage <= sortedData.state.length;

  const ui = (
    <div className={clsx(classes.tableContainer)}>
      {loading ? (
        <LinearProgress />
      ) : (
        <QueryResultTableBody
          useRowDataForEntityDrawer={useRowDataForEntityDrawer}
          columns={columns.state}
          sortedColumn={sortedColumn}
          sortedData={sortedData.state}
          sortAsc={sort === 'asc'}
          page={page}
          rowsPerPage={rowsPerPage}
          onSortClick={onSortClick}
          handleCellClick={handleClick}
          selectedRowID={
            parentSetSelectedRowID ? parentSelectedRowID : selectedRowID
          }
          rawData={result.data || []}
        />
      )}
    </div>
  );

  if (parentSetSelectedRowID)
    return (
      <>
        {ui}
        {showPaginationFooter && (
          <TablePagination
            count={
              hasNextPage
                ? sortedData.state.length + 1
                : sortedData.state.length
            }
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            component="div"
            labelDisplayedRows={({ from, to }) =>
              loading ? 'loading...' : `${from}-${to}`
            }
          />
        )}
      </>
    );

  return (
    <>
      <EntityDrawerHookProvider
        isDocked
        propertyFindRelated
        propertyFindSimilar
        onClose={() => setSelectedRowID(null)}
        noDataEdit={noDataEdit}
        onDeleteEntity={() => {
          if (result.type === 'list' && onResultsChange) {
            // For now, entity drawers can only be opened from landing in list mode because otherwise the ID of the selected entity may not exist.
            onResultsChange([
              {
                data: selectedRowID,
                type: QueryResultsChangeType.Delete,
              },
            ]);
          }
          setSelectedRowID(null);
        }}
      >
        {ui}
      </EntityDrawerHookProvider>

      {showPaginationFooter && (
        <TablePagination
          count={
            hasNextPage ? sortedData.state.length + 1 : sortedData.state.length
          }
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          component="div"
          labelDisplayedRows={({ from, to }) =>
            loading ? 'loading...' : `${from}-${to}`
          }
        />
      )}
    </>
  );
};

export default QueryResultTable;
